/**
 * RunStepChain - Claude Code-style execution trace, rendered inline in the
 * conversation flow.
 *
 * Every unit of work in a run (pipeline step, tool call, question, text block)
 * renders as a chain item: a colored status dot plus a body, linked to its
 * neighbours by a vertical rail. Nothing here is interactive or collapsible -
 * the trace IS the conversation. The live "what is it doing right now" line is
 * the RunStatusStrip above the composer.
 *
 * Dot semantics (see .ai-chat-chain__dot--* in features/ai-chat/ai-chat.css):
 *   running   - accent, pulsing
 *   completed - success green
 *   failed    - danger red
 *   waiting   - warning amber (live question)
 *   pending   - hollow outline
 *   skipped   - muted, faded
 *   neutral   - muted gray (text blocks, plain status notes)
 */

import type {
  ContextRetrievalStep,
  ExecutionStatus,
  ExecutionStep,
  GenericProgressStep,
  StepStatus,
  ToolCallRecord,
} from '@protoss/shared';
import * as React from 'react';
import { formatDuration } from '../../../lib/format';

// ─────────────────────────────────────────────────────────────────────────────
// Generic chain item
// ─────────────────────────────────────────────────────────────────────────────

export type ChainDot =
  | 'running'
  | 'completed'
  | 'failed'
  | 'waiting'
  | 'pending'
  | 'skipped'
  | 'neutral';

export interface ChainItemProps {
  dot: ChainDot;
  /** Extra modifier classes on the item (e.g. active-label shimmer). */
  className?: string;
  children: React.ReactNode;
}

export function ChainItem({ dot, className, children }: ChainItemProps) {
  return (
    <div
      className={`ai-chat-chain__item ai-chat-chain__item--${dot}${className ? ` ${className}` : ''}`}
      role="listitem"
    >
      <span className={`ai-chat-chain__dot ai-chat-chain__dot--${dot}`} aria-hidden="true" />
      <div className="ai-chat-chain__body">{children}</div>
    </div>
  );
}

/**
 * A result/detail sub-line under a chain item. Suppressed when empty or when it's
 * the generic "Completed" placeholder - the green dot already signals success, so
 * the word is redundant. Any other (meaningful) text still renders.
 */
export function ResultLine({ text }: { text?: string }) {
  if (!text || text.trim().toLowerCase() === 'completed') return null;
  return <span className="ai-chat-chain__result">{text}</span>;
}

function dotForStepStatus(status: StepStatus): ChainDot {
  switch (status) {
    case 'running':
      return 'running';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'skipped':
      return 'skipped';
    default:
      return 'pending';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Nested tool calls (tools that ran inside a pipeline step)
// ─────────────────────────────────────────────────────────────────────────────

function ToolCallRow({ toolCall }: { toolCall: ToolCallRecord }) {
  const dot: ChainDot =
    toolCall.status === 'failed'
      ? 'failed'
      : toolCall.status === 'running'
        ? 'running'
        : 'completed';
  return (
    <div className={`ai-chat-chain__tool ai-chat-chain__tool--${toolCall.status}`} role="listitem">
      <span className={`ai-chat-chain__dot ai-chat-chain__dot--${dot}`} aria-hidden="true" />
      <div className="ai-chat-chain__tool-body">
        <span className="ai-chat-chain__tool-label">
          {toolCall.toolLabel}
          {toolCall.durationMs !== undefined && toolCall.status === 'completed' && (
            <span className="ai-chat-chain__duration"> {formatDuration(toolCall.durationMs)}</span>
          )}
        </span>
        {toolCall.inputSummary && (
          <span className="ai-chat-chain__detail">{toolCall.inputSummary}</span>
        )}
        {toolCall.status === 'running' && toolCall.progressText && (
          <span className="ai-chat-chain__result">{toolCall.progressText}</span>
        )}
        {toolCall.status === 'completed' && <ResultLine text={toolCall.resultSummary} />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Evaluator step - self-review with reasoning/gaps as sub-lines (no confidence)
// ─────────────────────────────────────────────────────────────────────────────

function EvaluateRow({ step }: { step: GenericProgressStep }) {
  const gaps = step.gaps ?? [];

  return (
    <ChainItem dot="completed">
      <span className="ai-chat-chain__label">Reviewed answer</span>
      <ResultLine text={step.detail} />
      {gaps.length > 0 && (
        <ul className="ai-chat-chain__gaps">
          {gaps.map((gap, i) => (
            <li key={i} className="ai-chat-chain__gap">
              {gap}
            </li>
          ))}
        </ul>
      )}
    </ChainItem>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step rows - one chain item per user-facing execution step
// ─────────────────────────────────────────────────────────────────────────────

export interface StepChainRowsProps {
  steps: ExecutionStep[];
  activeStepId?: string;
}

/**
 * One execution step as a chain item. Exported so ChatRunView can render a single step at its
 * stream position when interleaving steps with text/tool segments (instead of one steps-only block).
 */
export function StepChainRow({
  step,
  activeStepId,
}: {
  step: ExecutionStep;
  activeStepId?: string;
}) {
  if (step.id === 'step-evaluate' && step.type === 'generic_progress') {
    return <EvaluateRow step={step as GenericProgressStep} />;
  }

  const toolCalls = (step as GenericProgressStep | ContextRetrievalStep).toolCalls ?? [];
  const isActive = step.id === activeStepId && step.status === 'running';

  // Persist the nested tool row through completion so the chain stays stable - a tool that shows
  // while a plan step runs (e.g. "Sales Deal Estimate Generator" under "Generate cost estimate")
  // must NOT vanish when the step completes (that appear-then-disappear flicker reads as a bug).
  // The completed row carries the inner tool's real duration + result summary.
  const visibleToolCalls = toolCalls;

  return (
    <ChainItem
      dot={dotForStepStatus(step.status)}
      className={isActive ? 'ai-chat-chain__item--active' : undefined}
    >
      <span className="ai-chat-chain__label">{step.label}</span>
      {step.type === 'tool_execution' && step.status === 'running' && step.progressText && (
        <span className="ai-chat-chain__result">{step.progressText}</span>
      )}
      {step.type === 'tool_execution' && step.status === 'completed' && (
        <ResultLine text={step.resultSummary} />
      )}
      {step.type !== 'tool_execution' && step.status === 'completed' && (
        <ResultLine text={step.detail} />
      )}
      {step.error && (
        <span className="ai-chat-chain__error" role="alert">
          {step.error}
        </span>
      )}
      {step.type === 'retrieve_context' &&
        step.status === 'completed' &&
        step.sourceCount !== undefined && (
          <span className="ai-chat-chain__meta">
            {step.citedCount ?? 0} / {step.sourceCount} sources cited
          </span>
        )}
      {visibleToolCalls.length > 0 && (
        <div className="ai-chat-chain__tools" role="list" aria-label="Tool calls">
          {visibleToolCalls.map((toolCall) => (
            <ToolCallRow key={toolCall.toolCallId} toolCall={toolCall} />
          ))}
        </div>
      )}
    </ChainItem>
  );
}

export function StepChainRows({ steps, activeStepId }: StepChainRowsProps) {
  return (
    <>
      {steps.map((step) => (
        <StepChainRow key={step.id} step={step} activeStepId={activeStepId} />
      ))}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Terminal summary row - closes the chain when the run is done
// ─────────────────────────────────────────────────────────────────────────────

export interface ChainSummaryRowProps {
  status: ExecutionStatus;
  modelLabel?: string;
}

export function ChainSummaryRow({ status, modelLabel }: ChainSummaryRowProps) {
  if (status.phase === 'failed') {
    return (
      <ChainItem dot="failed">
        <span className="ai-chat-chain__label">Run failed</span>
      </ChainItem>
    );
  }
  if (status.phase === 'cancelled') {
    return (
      <ChainItem dot="skipped">
        <span className="ai-chat-chain__label">Cancelled</span>
      </ChainItem>
    );
  }
  if (status.phase !== 'completed') return null;

  const timeText =
    status.durationMs !== undefined ? ` in ${formatDuration(status.durationMs)}` : '';
  return (
    <ChainItem dot="completed" className="ai-chat-chain__item--summary">
      <span className="ai-chat-chain__label">
        Done{timeText}
        {modelLabel && <span className="ai-chat-chain__model"> · {modelLabel}</span>}
      </span>
    </ChainItem>
  );
}
